import React, { useEffect, useMemo, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './LiveNetworkMap.css';
import { RingLoader } from 'react-spinners';
import { useNetworkStatus } from '../hooks/useNetworkStatus';

const DEFAULT_CENTER = [33.9617, -118.3531]; // Inglewood, CA
const DEFAULT_ZOOM = 11;

function escapePopupText(value) {
  if (value === undefined || value === null) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function buildMarkerIcon(isLive) {
  const markerColor = isLive ? '#28a745' : '#6c757d';
  const bgColor = isLive ? '#d4edda' : '#e9ecef';

  return L.icon({
    iconUrl: `data:image/svg+xml;base64,${btoa(`
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 36" width="48" height="36">
        <rect x="2" y="2" width="44" height="28" rx="2" fill="${bgColor}" stroke="${markerColor}" stroke-width="2.5"/>
        <rect x="5" y="5" width="38" height="22" rx="1" fill="${markerColor}" opacity="0.6"/>
        <rect x="19" y="30" width="10" height="2" rx="1" fill="${markerColor}"/>
        <rect x="22" y="28" width="4" height="4" fill="${markerColor}"/>
        <circle cx="42" cy="6" r="3" fill="${isLive ? '#28a745' : '#dc3545'}" stroke="white" stroke-width="1"/>
      </svg>
    `)}`,
    iconSize: [48, 36],
    iconAnchor: [24, 36],
    popupAnchor: [0, -36],
    className: 'screen-marker',
  });
}

function buildPopupHtml(screen, showLiveStatus) {
  const location = screen.locationDescription
    ? `<p style="margin:4px 0 0;font-size:13px;color:#64748b;">${escapePopupText(screen.locationDescription)}</p>`
    : '';
  const status = showLiveStatus
    ? '<p style="margin:6px 0 0;font-size:13px;">🟢 Online</p>'
    : '';
  return (
    `<div class="screen-popup">`
    + `<strong>${escapePopupText(screen.brand)}</strong>`
    + location
    + status
    + `</div>`
  );
}

function initializeMap(container, center, zoom) {
  const map = L.map(container).setView(center, zoom);
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; OpenStreetMap contributors',
    maxZoom: 19,
  }).addTo(map);
  return map;
}

export default function LiveNetworkMap({ className = '', showLiveStatus = true }) {
  const { screens, loading, usingFallback } = useNetworkStatus();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});

  const mappedScreens = useMemo(
    () => screens.filter((s) => s.latitude != null && s.longitude != null),
    [screens]
  );

  useEffect(() => {
    if (loading || !mapRef.current) return undefined;

    const timer = setTimeout(() => {
      if (!mapRef.current) return;

      const center = mappedScreens[0]
        ? [mappedScreens[0].latitude, mappedScreens[0].longitude]
        : DEFAULT_CENTER;
      const zoom = mappedScreens.length === 1 ? 14 : DEFAULT_ZOOM;

      if (!mapInstanceRef.current) {
        mapInstanceRef.current = initializeMap(mapRef.current, center, zoom);
      } else {
        mapInstanceRef.current.invalidateSize();
      }

      const map = mapInstanceRef.current;
      Object.values(markersRef.current).forEach((marker) => {
        map.removeLayer(marker);
      });
      markersRef.current = {};

      mappedScreens.forEach((screen) => {
        const marker = L.marker([screen.latitude, screen.longitude], {
          icon: buildMarkerIcon(true),
        })
          .bindPopup(buildPopupHtml(screen, showLiveStatus !== false))
          .addTo(map);
        markersRef.current[screen.screenId] = marker;
      });

      if (mappedScreens.length > 1) {
        const bounds = L.latLngBounds(
          mappedScreens.map((s) => [s.latitude, s.longitude])
        );
        map.fitBounds(bounds, { padding: [40, 40] });
      } else if (mappedScreens.length === 1) {
        map.setView([mappedScreens[0].latitude, mappedScreens[0].longitude], 14);
      } else {
        map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
      }
    }, 120);

    return () => clearTimeout(timer);
  }, [loading, mappedScreens, showLiveStatus]);

  useEffect(() => () => {
    if (mapInstanceRef.current) {
      try {
        mapInstanceRef.current.remove();
      } catch (e) {
        console.warn('Leaflet map cleanup:', e);
      }
      mapInstanceRef.current = null;
    }
  }, []);

  return (
    <div className={`invert-live-map relative rounded-2xl overflow-hidden border border-invert-border bg-slate-100 ${className}`}>
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80">
          <RingLoader color="#dc5024" loading size={36} aria-label="Loading map" />
        </div>
      )}

      {!loading && mappedScreens.length === 0 && (
        <div className="absolute bottom-3 left-3 right-3 z-10 pointer-events-none">
          <p className="text-xs text-slate-600 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 text-center border border-invert-border">
            {usingFallback
              ? 'Los Angeles network area — browse venue photos below.'
              : 'INVERT network — Los Angeles area.'}
          </p>
        </div>
      )}

      {!loading && mappedScreens.length > 0 && (
        <div className="absolute top-3 left-3 z-10 pointer-events-none">
          <p className="text-xs font-semibold text-invert-navy bg-white/90 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-invert-border shadow-sm">
            {mappedScreens.length} screen{mappedScreens.length === 1 ? '' : 's'} on the map
          </p>
        </div>
      )}

      <div
        ref={mapRef}
        className="w-full h-64 lg:h-96"
        aria-label="INVERT network map"
      />
    </div>
  );
}
